import { DatePicker } from '@/components/DatePicker'
import { FormDialog } from '@/components/FormDialog'
import { SelectedEquipmentCard } from '@/components/SelectedEquipmentCard'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { getProductName } from '@/features/equipment/utils/product'
import { useCreatePurchaseHistory } from '@/hooks/usePurchaseHistory'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetVendorProducts } from '../hooks/useVendors'
import type { Vendor } from '../types/vendor'

const emptyForm = {
  productId: '',
  price: '',
  quantity: '',
  date: '',
}
const MONEY_RE = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/
const INT_RE = /^(0|[1-9]\d*)$/

export function PurchaseHistoryDialog({
  vendor,
  open,
  onOpenChange,
}: {
  vendor: Vendor | null
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState(emptyForm)
  const { data: vendorProducts = [], isLoading: isLoadingVendorProducts } = useGetVendorProducts(vendor?.id, open)
  const createPurchaseHistory = useCreatePurchaseHistory()
  const selectedVendorProduct = vendorProducts.find(vp => vp.productId === form.productId)
  const productOptions = vendorProducts.map(vp => ({
    value: vp.productId,
    label: getProductName(vp.product.name, vp.product.brand, vp.product.model),
    description: vp.product.categoryName,
    searchValue: `${vp.product.name || ''} ${vp.product.brand || ''} ${vp.product.model || ''} ${vp.product.categoryName || ''}`,
  }))
  const isInvalid = !form.productId || !MONEY_RE.test(form.price) || !INT_RE.test(form.quantity) || !form.date

  useEffect(() => {
    if (!open) setForm(emptyForm)
  }, [open])

  async function handleSubmit() {
    if (isInvalid || !vendor) return

    try {
      await createPurchaseHistory.mutateAsync({
        productId: form.productId,
        vendorId: vendor.id,
        quotationId: null,
        date: form.date,
        price: form.price,
        quantity: form.quantity,
      })

      showSuccess(t('vendors.purchaseCreateSuccess'))
      onOpenChange(false)
    } catch (error) {
      if (isApiError(error)) showError(error.message)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('vendors.addPurchase')}
      onSubmit={handleSubmit}
      isLoading={createPurchaseHistory.isPending}
      isInvalid={isInvalid}
      contentTestId="purchase-history-dialog"
      submitTestId="purchase-history-form-submit"
    >
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('vendors.product')} *</Label>
        <SearchableSelect
          value={form.productId}
          onValueChange={productId => setForm({ ...form, productId })}
          options={productOptions}
          placeholder={t('vendors.selectProduct')}
          searchPlaceholder={t('vendors.searchProduct')}
          emptyText={isLoadingVendorProducts ? t('common.loading') : t('vendors.noProducts')}
          disabled={isLoadingVendorProducts}
          triggerClassName="w-full bg-secondary/50 text-xs"
          testId="purchase-history-form-product"
        />
      </div>
      {selectedVendorProduct && (
        <SelectedEquipmentCard
          name={selectedVendorProduct.product.name}
          brand={selectedVendorProduct.product.brand}
          model={selectedVendorProduct.product.model}
          categoryName={selectedVendorProduct.product.categoryName}
        />
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">
            {t('vendors.price')} ({t('vendors.baht')}) *
          </Label>
          <Input
            data-testid="purchase-history-form-price"
            inputMode="decimal"
            value={form.price}
            onChange={e => setForm({ ...form, price: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder="0.00"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('vendors.quantity')} *</Label>
          <Input
            data-testid="purchase-history-form-quantity"
            inputMode="numeric"
            value={form.quantity}
            onChange={e => setForm({ ...form, quantity: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder="1"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('common.date')} *</Label>
        <DatePicker
          testId="purchase-history-form-date"
          value={form.date}
          onChange={date => setForm({ ...form, date })}
        />
      </div>
    </FormDialog>
  )
}
